// Branding: Name, Claim, Farben, Logo und Links des Radios.
//
// Links stehen die Felder in Gruppen, rechts eine kleine Vorschau, die sofort
// zeigt, wie Farben und Logo zusammenspielen. Gespeichert wird erst mit dem Knopf.

import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { api } from "../api";
import { applyBranding } from "../branding";
import { Meldungen, Sym } from "../components/social-ui";
import { Card, Page, PageHeader, Skeleton } from "../components/ui";
import type { BrandingJson } from "../types";

const empty: BrandingJson = {
  name: "",
  claim: "",
  colors: { primary: "#ff5500", secondary: "#222222", accent: "#ffcc00" },
  logo_url: "",
  donate_url: "",
  newsletter_url: "",
  footer_md: "",
};

export function BrandingPage(): JSX.Element {
  const [branding, setBranding] = useState<BrandingJson>(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getBranding()
      .then((res) => {
        setBranding({ ...empty, ...res.branding, colors: { ...empty.colors, ...(res.branding.colors ?? {}) } });
        applyBranding(res.branding);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, []);

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const res = await api.updateBranding(branding);
      setBranding({ ...empty, ...res.branding, colors: { ...empty.colors, ...(res.branding.colors ?? {}) } });
      applyBranding(res.branding);
      setMessage("Branding gespeichert und an Public-App synchronisiert.");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page className="max-w-6xl">
        <PageHeader eyebrow="Aussehen" title="Branding" />
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]" aria-label="Lade Branding …">
          <Skeleton className="h-96" />
          <Skeleton className="h-72" />
        </div>
      </Page>
    );
  }

  const set = (patch: Partial<BrandingJson>): void => setBranding({ ...branding, ...patch });
  const setColor = (key: "primary" | "secondary" | "accent", v: string): void =>
    setBranding({ ...branding, colors: { ...branding.colors, [key]: v } });

  return (
    <Page className="max-w-6xl">
      <PageHeader
        eyebrow="Aussehen"
        title="Branding"
        description="Name, Claim, Farben und Logo eures Radios – so erscheint es auf der Hörerseite und in der Suite."
      />
      <form onSubmit={onSubmit} className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-5">
          <Card title="Identität" description="Wie euer Radio heißt und wofür es steht.">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" value={branding.name ?? ""} onChange={(v) => set({ name: v })} placeholder="Radio Maar" />
              <Field label="Claim" value={branding.claim ?? ""} onChange={(v) => set({ claim: v })} placeholder="Der Sound der Vulkaneifel" />
              <div className="sm:col-span-2">
                <Field label="Logo-URL" value={branding.logo_url ?? ""} onChange={(v) => set({ logo_url: v })} placeholder="https://…" icon="image" />
              </div>
            </div>
          </Card>

          <Card title="Farben" description="Die Primärfarbe ist die Hausfarbe: Knöpfe, Hervorhebungen, aktive Reiter.">
            <div className="grid gap-4 sm:grid-cols-3">
              <ColorField label="Primärfarbe" value={branding.colors?.primary ?? ""} onChange={(v) => setColor("primary", v)} />
              <ColorField label="Sekundärfarbe" value={branding.colors?.secondary ?? ""} onChange={(v) => setColor("secondary", v)} />
              <ColorField label="Akzentfarbe" value={branding.colors?.accent ?? ""} onChange={(v) => setColor("accent", v)} />
            </div>
          </Card>

          <Card title="Links und Fußzeile">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Spenden-URL" value={branding.donate_url ?? ""} onChange={(v) => set({ donate_url: v })} placeholder="https://…" icon="heart" />
              <Field label="Newsletter-URL" value={branding.newsletter_url ?? ""} onChange={(v) => set({ newsletter_url: v })} placeholder="https://…" icon="mail" />
              <label className="block sm:col-span-2">
                <span className="field-label">Footer (Markdown)</span>
                <textarea
                  className="input font-mono text-[0.8rem] leading-relaxed"
                  rows={3}
                  value={branding.footer_md ?? ""}
                  onChange={(e) => set({ footer_md: e.target.value })}
                />
              </label>
            </div>
          </Card>

          <div>
            <Meldungen error={error} notice={message} onClear={() => setMessage(null)} />
            <div className="flex flex-wrap items-center gap-3">
              <button type="submit" disabled={saving} className="btn btn-primary">
                {saving ? "Speichern …" : "Speichern"}
              </button>
              <span className="text-xs text-muted">Die Hörerseite übernimmt die Änderungen gleich nach dem Speichern.</span>
            </div>
          </div>
        </div>

        <aside className="min-w-0 lg:sticky lg:top-4">
          <div className="section-label mb-2">Vorschau</div>
          <Vorschau branding={branding} />
        </aside>
      </form>
    </Page>
  );
}

/** Eine Miniatur der Hörerseite – nur zur Orientierung, nicht pixelgenau. */
function Vorschau({ branding }: { branding: BrandingJson }): JSX.Element {
  const primary = branding.colors?.primary || "#ff5500";
  const secondary = branding.colors?.secondary || "#222222";
  const accent = branding.colors?.accent || "#ffcc00";
  const name = branding.name || "Euer Radio";
  return (
    <div className="card overflow-hidden">
      <div className="px-4 pb-5 pt-4 text-white" style={{ background: `linear-gradient(135deg, ${secondary}, ${secondary}dd)` }}>
        <div className="flex items-center gap-3">
          {branding.logo_url
            ? <img src={branding.logo_url} alt="" className="h-10 w-10 rounded-lg bg-white/10 object-contain" />
            : <span className="flex h-10 w-10 items-center justify-center rounded-lg text-sm font-bold" style={{ background: primary }}>{name.slice(0, 2).toUpperCase()}</span>}
          <div className="min-w-0">
            <div className="truncate font-semibold">{name}</div>
            {branding.claim && <div className="truncate text-xs opacity-75">{branding.claim}</div>}
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full shadow-lg" style={{ background: primary }}>
            <Sym name="play" className="ml-0.5 h-4 w-4 fill-current" />
          </span>
          <div className="min-w-0 text-xs">
            <div className="flex items-center gap-1.5 font-semibold uppercase tracking-wider" style={{ color: accent }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} />Live
            </div>
            <div className="truncate opacity-90">Gerade läuft: Morgenshow</div>
          </div>
        </div>
      </div>
      <div className="space-y-3 p-4">
        <div className="flex gap-2">
          <span className="rounded-md px-3 py-1.5 text-xs font-medium text-white" style={{ background: primary }}>Wunsch senden</span>
          <span className="rounded-md border px-3 py-1.5 text-xs font-medium" style={{ borderColor: primary, color: primary }}>Programm</span>
        </div>
        <div className="flex gap-2 text-xs text-muted">
          {branding.donate_url && <span className="inline-flex items-center gap-1"><Sym name="heart" className="h-3 w-3" />Spenden</span>}
          {branding.newsletter_url && <span className="inline-flex items-center gap-1"><Sym name="mail" className="h-3 w-3" />Newsletter</span>}
        </div>
        <div className="flex gap-1.5 pt-1">
          {[primary, secondary, accent].map((c, i) => (
            <span key={i} className="h-5 flex-1 rounded" style={{ background: c, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }} title={c} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, icon }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; icon?: string }): JSX.Element {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <span className="relative block">
        {icon && <Sym name={icon} className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />}
        <input
          className={`input ${icon ? "pl-9" : ""}`}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      </span>
    </label>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }): ReactNode {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <div className="flex items-center gap-2">
        <span className="relative h-[2.375rem] w-[2.375rem] flex-shrink-0 overflow-hidden rounded-lg border border-default" style={{ background: value || "#000000" }}>
          <input
            type="color"
            value={value || "#000000"}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            aria-label={`${label} wählen`}
          />
        </span>
        <input
          type="text"
          className="input min-w-0 font-mono text-xs uppercase"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </label>
  );
}
